import React from "react";

import { TableTitle } from "@frontend/common";
import {
  TableContainer,
  TableRowContainer,
  TableShellTitle,
  TableShellValue,
} from "@frontend/common/src/shared/components/core";

import EditableTextContainer from "@EditArticleContainer/shared/components/EditableTextContainer/EditableTextContainer";
import { useCreateCompanyLogoComponentList } from "@EditArticleContainer/shared/hooks/useCreateCompanyLogoComponentList";
import {
  REF_CODE_EXPEDITED_PROGRAM_DESIGNATION_BASIS_AND_CLINICAL_SIGNIFICANCE,
  REF_CODE_EXPEDITED_PROGRAM_DESIGNATED_COMPANY_NAME,
  REF_CODE_EXPEDITED_PROGRAM_DESIGNATED_INDICATION,
  REF_CODE_EXPEDITED_PROGRAM_EXPEDITED_PROGRAM,
  REF_CODE_EXPEDITED_PROGRAM_REGULATORY_AGENCY,
  REF_CODE_EXPEDITED_PROGRAM_UPDATE_DATE,
} from "@EditArticleContainer/shared/libs/refCode/constants/refCode.constants";
import { createRefCode } from "@EditArticleContainer/shared/libs/refCode/utils/createRefCode";
import { ParagraphContent } from "@EditArticleContainer/shared/types/paragraphContents/paragraphContents.types";
import { formattedArticleDate } from "@EditArticleContainer/shared/utils/formattedArticleDate";
import { createMultiLineText } from "@utils/createMultiLineText/createMultiLineText";
import { padStart } from "@utils/padStart/padStart";

import * as S from "./ExpeditedProgramTable.style";

const GROUP_TABLE_TITLE_STYLE = {
  height: "3.3rem",
};

interface Props {
  tableData: ParagraphContent["TC314"]["text"]["Expedited Program Contents"][0];
  tableIndex: number;
  multiTable: boolean;
}

const COMMON_EDITABLE_TEXT_STYLE = {
  padding: "0.6rem",
  borderRadius: "0",
};
const VALUE_SHELL_PADDING = "0.2rem";

const ExpeditedProgramTable = ({
  tableData,
  tableIndex,
  multiTable,
}: Props) => {
  const { createCompanyLogoComponentList } =
    useCreateCompanyLogoComponentList();
  // NOTE: Regulatory Agency 값이 정규화가 된 이후에 국기 표출 로직 활성화 필요
  // const agencyCountryData = useAgencyCountryQuery();

  const expeditedProgramValueText = {
    "Update Date": tableData["Update Date"]?.value?.at(0)?.text || "",
    "Regulatory Agency":
      tableData["Regulatory Agency"]?.value?.at(0)?.text || "",
    "Designated Company name": createMultiLineText({
      textList:
        tableData?.["Designated Company name"]?.value.map?.(
          (item) => item.text ?? "",
        ) ?? [],
      parseString: (text) => `- ${text?.trim()}`,
    }),
    "Expedited Program": createMultiLineText({
      textList:
        tableData?.["Expedited Program"]?.value.map?.(
          (item) => item.text ?? "",
        ) ?? [],
      parseString: (text) => `- ${text?.trim()}`,
    }),
    "Designated Indication": createMultiLineText({
      textList:
        tableData?.["Designated Indication"]?.value.map?.(
          (item) => item.text ?? "",
        ) ?? [],
      parseString: (text) => `- ${text?.trim()}`,
    }),
    "Designation Basis & Clinical Significance": createMultiLineText({
      textList:
        tableData?.["Designation Basis & Clinical Significance"]?.value.map?.(
          (item) => item.text ?? "",
        ) ?? [],
      parseString: (text) => `- ${text?.trim()}`,
    }),
  };

  const expeditedProgramValueLabels = {
    "Update Date": tableData["Update Date"]?.value?.at(0)?.labels || [],
    "Regulatory Agency":
      tableData["Regulatory Agency"]?.value?.at(0)?.labels || [],
    "Designated Company name":
      tableData["Designated Company name"]?.value?.at(0)?.labels || [],
    "Expedited Program":
      tableData["Expedited Program"]?.value?.at(0)?.labels || [],
    "Designated Indication":
      tableData["Designated Indication"]?.value?.at(0)?.labels || [],
    "Designation Basis & Clinical Significance":
      tableData["Designation Basis & Clinical Significance"]?.value?.at(0)
        ?.labels || [],
  };
  const tableTitle = `Expedited Program ${padStart(tableIndex + 1)} - ${
    expeditedProgramValueText["Expedited Program"]
  }`;
  return (
    <S.TableAndTitleWrapper>
      {multiTable && (
        <TableTitle title={tableTitle} style={GROUP_TABLE_TITLE_STYLE} />
      )}
      <S.TablesWrapper>
        <TableContainer>
          <TableRowContainer>
            <TableShellTitle width="25rem">Update Date</TableShellTitle>
            <TableShellTitle width="25rem">Regulatory Agency</TableShellTitle>
            <TableShellTitle>Designated Company</TableShellTitle>
          </TableRowContainer>
          <TableRowContainer>
            <TableShellValue width="25rem" padding={VALUE_SHELL_PADDING}>
              <EditableTextContainer
                item={{
                  text:
                    formattedArticleDate(
                      expeditedProgramValueText["Update Date"],
                    ) || "",
                  labels: expeditedProgramValueLabels["Update Date"],
                }}
                refKey={createRefCode(
                  REF_CODE_EXPEDITED_PROGRAM_UPDATE_DATE,
                  tableIndex,
                )}
                canTextEdit
                style={COMMON_EDITABLE_TEXT_STYLE}
              />
            </TableShellValue>
            <TableShellValue width="25rem" padding={VALUE_SHELL_PADDING}>
              <EditableTextContainer
                item={{
                  text: expeditedProgramValueText["Regulatory Agency"],
                  labels: expeditedProgramValueLabels["Regulatory Agency"],
                }}
                refKey={createRefCode(
                  REF_CODE_EXPEDITED_PROGRAM_REGULATORY_AGENCY,
                  tableIndex,
                )}
                canTextEdit
                isDisabled
                // NOTE: Regulatory Agency 값이 정규화가 된 이후에 국기 표출 로직 활성화 필요
                // imagePosition="left"
                // imageNodeList={[
                //   <RegulatoryAgencyIcon
                //     isTopMargin
                //     agencyType={expeditedProgramValueText["Regulatory Agency"]}
                //     agencyCountryMap={agencyCountryData}
                //   />,
                // ]}
                style={COMMON_EDITABLE_TEXT_STYLE}
              />
            </TableShellValue>
            <TableShellValue padding={VALUE_SHELL_PADDING}>
              <EditableTextContainer
                item={{
                  text: expeditedProgramValueText["Designated Company name"],
                  labels:
                    expeditedProgramValueLabels["Designated Company name"],
                }}
                refKey={createRefCode(
                  REF_CODE_EXPEDITED_PROGRAM_DESIGNATED_COMPANY_NAME,
                  tableIndex,
                )}
                canTextEdit
                isDisabled
                imagePosition="left"
                imageNodeList={createCompanyLogoComponentList(
                  tableData?.["Designated Company name"]?.value?.flatMap?.(
                    (item) => item.text?.replaceAll("- ", "").split(`\n`) ?? "",
                  ) ?? [],
                )}
                style={COMMON_EDITABLE_TEXT_STYLE}
              />
            </TableShellValue>
          </TableRowContainer>
          <TableRowContainer>
            <TableShellTitle>
              Expedited & Incentive Program Name
            </TableShellTitle>
          </TableRowContainer>
          <TableRowContainer>
            <TableShellValue padding={VALUE_SHELL_PADDING}>
              <EditableTextContainer
                item={{
                  text: expeditedProgramValueText["Expedited Program"],
                  labels: expeditedProgramValueLabels["Expedited Program"],
                }}
                refKey={createRefCode(
                  REF_CODE_EXPEDITED_PROGRAM_EXPEDITED_PROGRAM,
                  tableIndex,
                )}
                canTextEdit
                style={COMMON_EDITABLE_TEXT_STYLE}
              />
            </TableShellValue>
          </TableRowContainer>

          <>
            <TableRowContainer
              isDisplay={!!expeditedProgramValueText["Designated Indication"]}
            >
              <TableShellTitle>Designated Indication</TableShellTitle>
            </TableRowContainer>
            <TableRowContainer
              isDisplay={!!expeditedProgramValueText["Designated Indication"]}
            >
              <TableShellValue padding={VALUE_SHELL_PADDING}>
                <EditableTextContainer
                  item={{
                    text: expeditedProgramValueText["Designated Indication"],
                    labels:
                      expeditedProgramValueLabels["Designated Indication"],
                  }}
                  refKey={createRefCode(
                    REF_CODE_EXPEDITED_PROGRAM_DESIGNATED_INDICATION,
                    tableIndex,
                  )}
                  canTextEdit
                  style={COMMON_EDITABLE_TEXT_STYLE}
                />
              </TableShellValue>
            </TableRowContainer>
          </>
          <>
            <TableRowContainer
              isDisplay={
                !!expeditedProgramValueText[
                  "Designation Basis & Clinical Significance"
                ]
              }
            >
              <TableShellTitle>Designation Basis</TableShellTitle>
            </TableRowContainer>
            <TableRowContainer
              isDisplay={
                !!expeditedProgramValueText[
                  "Designation Basis & Clinical Significance"
                ]
              }
            >
              <TableShellValue padding={VALUE_SHELL_PADDING}>
                <EditableTextContainer
                  item={{
                    text: expeditedProgramValueText[
                      "Designation Basis & Clinical Significance"
                    ],
                    labels:
                      expeditedProgramValueLabels[
                        "Designation Basis & Clinical Significance"
                      ],
                  }}
                  refKey={createRefCode(
                    REF_CODE_EXPEDITED_PROGRAM_DESIGNATION_BASIS_AND_CLINICAL_SIGNIFICANCE,
                    tableIndex,
                  )}
                  canTextEdit
                  style={COMMON_EDITABLE_TEXT_STYLE}
                />
              </TableShellValue>
            </TableRowContainer>
          </>
        </TableContainer>
      </S.TablesWrapper>
    </S.TableAndTitleWrapper>
  );
};

export default ExpeditedProgramTable;
